import type { AiNotes } from './ai-notes';
import { footageTime, type FileRef, type Footage } from './footage';
import { formatDate } from './format';
import type { Moment } from './moments';
import { bestTier, byRank } from './ranking';
import type { EventInfo, ReasonKind } from './reasons';
import { onDay, parseSearch } from './search';
import { groupSessions } from './sessions';
import { wallNow } from './time';

const INCIDENT_MAX_TIER = 3;
const SENTRY_REASON_PREFIX = 'sentry_';

export const CHIPS = ['all', 'incidents', 'driving', 'saved', 'sentry', 'clips'] as const;
export type Chip = (typeof CHIPS)[number];

export const SORT_ORDERS = ['rank', 'newest', 'oldest', 'type'] as const;
export type SortOrder = (typeof SORT_ORDERS)[number];

const KIND_ORDER: readonly ReasonKind[] = ['bump', 'aeb', 'handle', 'manual', 'honk', 'object', 'unknown', 'none'];
const DAY_MS = 86_400_000;

export interface PlacedMoment extends Moment {
  readonly at: number;
}

export interface EventEntry<F extends FileRef> {
  readonly id: string;
  readonly footage: Footage<F>;
  readonly info: EventInfo | undefined;
  readonly time: number;
  readonly latitude: number | undefined;
  readonly longitude: number | undefined;
  readonly sentry: boolean;
  readonly moments: readonly PlacedMoment[];
  readonly notes: AiNotes | undefined;
  readonly tier: number;
}

interface ItemBase {
  readonly id: string;
  readonly tier: number;
  readonly time: number;
}

export type TriageItem<F extends FileRef> =
  | (ItemBase & { readonly kind: 'event'; readonly entry: EventEntry<F> })
  | (ItemBase & { readonly kind: 'session'; readonly entries: readonly EventEntry<F>[]; readonly lead: EventEntry<F> });

export function eventEntry<F extends FileRef>(footage: Footage<F>, info: EventInfo | undefined, moments: readonly PlacedMoment[], notes?: AiNotes): EventEntry<F> {
  return {
    id: footage.id,
    footage,
    info,
    time: footageTime(footage),
    latitude: info?.latitude,
    longitude: info?.longitude,
    sentry: footage.kind === 'sentry' || (info?.reason.raw.startsWith(SENTRY_REASON_PREFIX) ?? false),
    moments,
    notes,
    tier: bestTier(info?.reason, moments, notes),
  };
}

const eventItem = <F extends FileRef>(entry: EventEntry<F>): TriageItem<F> => ({ kind: 'event', id: entry.id, tier: entry.tier, time: entry.time, entry });

function sessionItem<F extends FileRef>(entries: readonly EventEntry<F>[]): TriageItem<F> {
  const lead = [...entries].sort(byRank)[0]!;
  if (entries.length === 1) return eventItem(lead);
  return { kind: 'session', id: `session:${entries[0]!.id}`, tier: lead.tier, time: lead.time, entries, lead };
}

export function buildTriage<F extends FileRef>(entries: readonly EventEntry<F>[]): readonly TriageItem<F>[] {
  const sessions = groupSessions(entries.filter((entry) => entry.sentry)).map(sessionItem);
  const standalone = entries.filter((entry) => !entry.sentry).map(eventItem);
  return [...standalone, ...sessions].sort(byRank);
}

export const entriesOf = <F extends FileRef>(item: TriageItem<F>): readonly EventEntry<F>[] =>
  item.kind === 'event' ? [item.entry] : item.entries;

const CHIP_TESTS: Readonly<Record<Chip, (entry: EventEntry<FileRef>) => boolean>> = {
  all: () => true,
  incidents: (entry) => entry.tier <= INCIDENT_MAX_TIER,
  driving: (entry) => entry.moments.length > 0,
  saved: (entry) => !entry.sentry && entry.info !== undefined,
  sentry: (entry) => entry.sentry,
  clips: (entry) => !entry.sentry && entry.info === undefined,
};

const notesText = (notes: AiNotes | undefined): readonly string[] =>
  notes ? [notes.summary, ...notes.tags, ...notes.moments.flatMap((moment) => [moment.label, moment.plate])] : [];

const searchText = (entry: EventEntry<FileRef>): string =>
  [entry.info?.city, entry.info?.street, entry.info?.reason.label, entry.footage.name, formatDate(entry.time), ...notesText(entry.notes)].join(' ').toLowerCase();

export function filterTriage<F extends FileRef>(items: readonly TriageItem<F>[], chip: Chip, query: string, today = wallNow()): readonly TriageItem<F>[] {
  const { days, words } = parseSearch(query, today);
  const keep = (entry: EventEntry<F>): boolean =>
    CHIP_TESTS[chip](entry) && days.every((day) => onDay(day, entry.time)) && words.every((word) => searchText(entry).includes(word));
  return items.filter((item) => entriesOf(item).some(keep));
}

export const countChips = <F extends FileRef>(items: readonly TriageItem<F>[]): Readonly<Record<Chip, number>> =>
  Object.fromEntries(CHIPS.map((chip) => [chip, filterTriage(items, chip, '').length])) as Record<Chip, number>;

const leadOf = <F extends FileRef>(item: TriageItem<F>): EventEntry<F> => (item.kind === 'event' ? item.entry : item.lead);

const kindRank = <F extends FileRef>(item: TriageItem<F>): number => {
  const kind = leadOf(item).info?.reason.kind;
  return kind === undefined ? KIND_ORDER.length : KIND_ORDER.indexOf(kind);
};

const SORTERS: Readonly<Record<SortOrder, (a: TriageItem<FileRef>, b: TriageItem<FileRef>) => number>> = {
  rank: byRank,
  newest: (a, b) => b.time - a.time,
  oldest: (a, b) => a.time - b.time,
  type: (a, b) => kindRank(a) - kindRank(b) || b.time - a.time,
};

export const sortTriage = <F extends FileRef>(items: readonly TriageItem<F>[], order: SortOrder): readonly TriageItem<F>[] => [...items].sort(SORTERS[order]);

export function sectionOf<F extends FileRef>(item: TriageItem<F>, order: SortOrder): string | undefined {
  if (order === 'type') return leadOf(item).info?.reason.label ?? 'Recordings';
  if (order === 'newest' || order === 'oldest') return formatDate(Math.floor(item.time / DAY_MS) * DAY_MS);
  return undefined;
}
